/**
 * The Nado withdrawal money path: generate a Fun deposit address, sign Nado's
 * gasless `withdraw_collateral_v2` with `sendTo` pointing at it, and submit.
 *
 * Port of web's `createNadoWithdrawalCallback` over {@link WithdrawalClient}.
 * Two differences from web:
 * - The amount arrives **already net** of the sequencer fee, where web's
 *   callback deducts it itself. Deducting again would short the user a fee.
 * - Borrowing is never offered, so `spot_leverage` is always `false`.
 *
 * Re-submitting belongs to the gateway, which reuses the signed nonce; a retry
 * from here would mint a fresh one and withdraw twice.
 */

import {
  type FunAddress,
  NADO_API_KEY,
  initializeCheckoutTokenTransferAddress,
} from '@funkit/api-base'
import {
  type WithdrawalClient,
  isAddressEquivalent,
  isUserRejectedError,
  normalizeRecipientAddress,
  safeBigInt,
  toWithdrawalAmountBaseUnit,
} from '@funkit/connect-core'
import {
  INK_CHAIN_ID,
  NADO_WITHDRAWAL_ACTION_TYPE,
  NadoGatewayError,
  type NadoWithdrawalSourceOption,
  NadoWithdrawalUnconfirmedError,
  buildWithdrawCollateralV2TypedData,
  executeNadoWithdrawCollateralV2,
  getNadoMaxWithdrawable,
  getNadoTxNonce,
  isExpectedNadoWithdrawalFailure,
  x18ToRawBaseUnit,
} from '@funkit/connect-core/clients/nado'
import type { Address, Hex } from 'viem'

import type { CustomWithdrawalSubmission } from '../../src/flow/withdrawalFlowConfig'
import { i18n } from '../../src/locales/i18n'
import { logger } from '../../src/logging/logger'
import type { NadoWithdrawalFlowConfig } from './nadoWithdrawalConfig'
import { NADO_MOBILE_SPOT_LEVERAGE } from './nadoWithdrawalConfig'
import { toNadoWithdrawalErrorMessage } from './withdrawalErrors'

/** Steps are logged on failure so a support ticket names the stage it died at. */
type NadoWithdrawalStep =
  | 'validate'
  | 'beforeSign'
  | 'switchChain'
  | 'uda'
  | 'sign'
  | 'execute'

/** A wiring bug, not a user-correctable condition; the user gets the generic line. */
class NadoWithdrawalInputError extends Error {}

/** Already logged where it was raised, so the catch neither re-logs nor escalates. */
class NadoWithdrawalUserError extends Error {}

export interface NadoWithdrawalSubmitContext {
  nado: NadoWithdrawalFlowConfig
  /** Packed id of the subaccount being debited (`buildNadoSubaccountId`). */
  subaccount: Hex
  sourceToken: NadoWithdrawalSourceOption
  /** The host's signer. Nado needs `signTypedData`; a client without it fails. */
  wallet: WithdrawalClient
  /** Drops the cached max, or a follow-up withdrawal opens on a stale balance. */
  onWithdrawn: () => void
}

/**
 * The `submitWithdrawal` the crypto rail runs in place of a relay execution.
 * Resolves with the deposit address Nado was told to send to — nothing of ours
 * goes on-chain, so there is no transaction to return.
 */
export function createNadoWithdrawalSubmit({
  nado,
  subaccount,
  sourceToken,
  wallet,
  onWithdrawn,
}: NadoWithdrawalSubmitContext) {
  return async ({
    recipientAddress,
    destinationToken,
    amountTokenUnits,
  }: CustomWithdrawalSubmission): Promise<{ depositAddress: Address }> => {
    const { walletAddress, onBeforeSign } = nado
    const { productId, decimals } = sourceToken
    const recipientAddr = normalizeRecipientAddress(recipientAddress)

    let step: NadoWithdrawalStep = 'validate'

    try {
      // Already net of the fee — see the note at the top of the file.
      const netRawAmount = toWithdrawalAmountBaseUnit(
        amountTokenUnits,
        decimals,
      )
      if (netRawAmount <= 0n) {
        logger.warn('nado:withdrawal:amountTooLow', {
          productId,
          amountTokenUnits,
        })
        throw new NadoWithdrawalUserError(
          i18n.t('withdrawNado.errorAmountTooLow'),
        )
      }

      logger.info('nado:withdrawal:start', {
        productId,
        spotLeverage: NADO_MOBILE_SPOT_LEVERAGE,
        decimals,
        amountTokenUnits,
        toChainId: destinationToken.chainId,
        toToken: destinationToken.address,
        recipientAddress: recipientAddr,
      })

      await assertWithinMaxWithdrawable({
        subaccount,
        productId,
        decimals,
        netRawAmount,
      })

      // Before the deposit address: a declined switch or swapped account would
      // otherwise orphan one.
      step = 'beforeSign'
      try {
        await onBeforeSign?.()
      } catch (error) {
        // The documented way for a host to abort — a user choice, not a failure.
        const message = error instanceof Error ? error.message : String(error)
        logger.warn('nado:withdrawal:abortedByHost', {
          walletAddress,
          errorMessage: message,
        })
        throw new NadoWithdrawalUserError(message)
      }

      step = 'switchChain'
      await ensureWalletOnInk(wallet)
      await assertOwnerIsSigner({ walletAddress, wallet })

      step = 'uda'
      const { depositAddr } = await initializeCheckoutTokenTransferAddress({
        apiKey: NADO_API_KEY,
        // The owner wallet is the stable per-user id, and is what the completion
        // tracker later looks the arrival up by.
        userId: walletAddress,
        recipientAddr,
        toChainId: destinationToken.chainId,
        toTokenAddress: destinationToken.address,
        sourceOfFund: `balance|nado|${walletAddress}`,
        // Attribution only — forces a separate address from Nado deposits.
        actionType: NADO_WITHDRAWAL_ACTION_TYPE,
        logger,
        clientMetadata: { isWithdrawal: true },
      })
      logger.info('nado:withdrawal:udaGenerated', {
        depositAddr,
        productId,
        recipientAddress: recipientAddr,
      })

      step = 'sign'
      // Immediately before signing: a stale nonce is rejected outright.
      const nonce = await getNadoTxNonce(walletAddress, { logger })
      const signature = await signWithdrawal({
        wallet,
        subaccount,
        productId,
        netRawAmount,
        nonce,
        sendTo: depositAddr as Address,
      })
      logger.info('nado:withdrawal:signed', {
        nonce: nonce.toString(),
        depositAddr,
        productId,
      })

      step = 'execute'
      try {
        await executeNadoWithdrawCollateralV2(
          {
            sender: subaccount,
            productId,
            amount: netRawAmount,
            nonce,
            sendTo: depositAddr,
            signature,
            spotLeverage: NADO_MOBILE_SPOT_LEVERAGE,
          },
          { logger },
        )
      } catch (error) {
        // An unconfirmed execute may still have debited. Re-reading Nado is
        // right either way; keeping the cached maximum is only right if it did
        // not land.
        if (error instanceof NadoWithdrawalUnconfirmedError) {
          onWithdrawn()
        }
        throw error
      }
      logger.info('nado:withdrawal:executeSubmitted', {
        nonce: nonce.toString(),
        depositAddr,
        productId,
        netRawAmount: netRawAmount.toString(),
      })
      onWithdrawn()

      return { depositAddress: depositAddr as Address }
    } catch (error) {
      throw toThrownWithdrawalError(error, {
        step,
        walletAddress,
        subaccount,
        recipientAddress: recipientAddr,
        toChainId: destinationToken.chainId,
      })
    }
  }
}

/**
 * The signature is off-chain and gasless, but wallets reject an
 * `eth_signTypedData_v4` whose domain chainId isn't the active chain — so the
 * wallet still has to sit on Ink to sign it.
 */
async function ensureWalletOnInk(wallet: WithdrawalClient): Promise<void> {
  const connectedChainId = await wallet.getChainId()
  if (connectedChainId === INK_CHAIN_ID) {
    return
  }
  logger.info('nado:withdrawal:switchingChain', {
    fromChainId: connectedChainId,
  })
  try {
    await wallet.switchChain(INK_CHAIN_ID)
  } catch (error) {
    if (isUserRejectedError(error)) {
      throw error
    }
    logger.warn('nado:withdrawal:switchChainFailed', {
      fromChainId: connectedChainId,
      errorMessage: error instanceof Error ? error.message : String(error),
    })
    throw new NadoWithdrawalUserError(i18n.t('withdrawCrypto.errorSwitchChain'))
  }
}

/** Nado only honors owner-wallet signatures, so a switched account must fail loudly. */
async function assertOwnerIsSigner({
  walletAddress,
  wallet,
}: {
  walletAddress: Address
  wallet: WithdrawalClient
}): Promise<void> {
  const connectedAddress = wallet.address()
  if (
    connectedAddress &&
    isAddressEquivalent({
      firstAddress: connectedAddress,
      secondAddress: walletAddress,
    })
  ) {
    return
  }
  logger.warn('nado:withdrawal:signerMismatch', {
    walletAddress,
    connectedAddress: connectedAddress ?? null,
  })
  throw new NadoWithdrawalUserError(i18n.t('withdrawNado.errorSignerMismatch'))
}

/** `signTypedData` is optional on the client, but Nado cannot withdraw without it. */
async function signWithdrawal({
  wallet,
  subaccount,
  productId,
  netRawAmount,
  nonce,
  sendTo,
}: {
  wallet: WithdrawalClient
  subaccount: Hex
  productId: number
  netRawAmount: bigint
  nonce: bigint
  sendTo: Address
}): Promise<Hex> {
  if (!wallet.signTypedData) {
    throw new NadoWithdrawalInputError(
      'withdrawalWallet cannot signTypedData, which Nado withdrawals require',
    )
  }
  const signature = await wallet.signTypedData(
    buildWithdrawCollateralV2TypedData({
      sender: subaccount,
      productId,
      amount: netRawAmount,
      nonce,
      sendTo,
    }),
  )
  return signature as Hex
}

/**
 * Race backstop, not the primary gate: the amount screen already blocks this,
 * but account health can move between render and submit — and catching it here
 * saves a wallet prompt and a burnt nonce. `max_withdrawable` is already net of
 * the fee, so it compares against the signed amount.
 */
async function assertWithinMaxWithdrawable({
  subaccount,
  productId,
  decimals,
  netRawAmount,
}: {
  subaccount: Hex
  productId: number
  decimals: number
  netRawAmount: bigint
}): Promise<void> {
  const maxWithdrawableX18 = await getNadoMaxWithdrawable(
    { productId, sender: subaccount, spotLeverage: NADO_MOBILE_SPOT_LEVERAGE },
    { logger },
  )
  const maxRawAmount = x18ToRawBaseUnit(
    safeBigInt(maxWithdrawableX18, 0n),
    decimals,
  )
  if (netRawAmount <= maxRawAmount) {
    return
  }
  logger.warn('nado:withdrawal:exceedsMaxWithdrawable', {
    productId,
    netRawAmount: netRawAmount.toString(),
    maxRawAmount: maxRawAmount.toString(),
  })
  // No figure in the copy: on a race it is stale by the time it is read.
  throw new NadoWithdrawalUserError(i18n.t('withdrawNado.errorBalanceChanged'))
}

/**
 * Failure → what the user sees, and at what severity.
 *
 * - Recovered states (declined signature, precondition rejections, expected
 *   gateway codes) log at WARN; ERROR is reserved for what is worth alerting on.
 * - Gateway failures map to Nado copy; everything else already carries a usable
 *   message and passes through.
 */
function toThrownWithdrawalError(
  error: unknown,
  context: {
    step: NadoWithdrawalStep
    walletAddress: Address
    subaccount: Hex
    recipientAddress: FunAddress
    toChainId: string
  },
): unknown {
  const logContext = {
    ...context,
    nadoErrorCode:
      error instanceof NadoGatewayError ? (error.errorCode ?? null) : null,
    nadoErrorKind: error instanceof NadoGatewayError ? error.kind : null,
    errorMessage: error instanceof Error ? error.message : String(error),
  }
  // A user choice, not a failure to alert on.
  if (isUserRejectedError(error)) {
    logger.warn('nado:withdrawal:rejected', logContext)
    return error
  }
  // Already warned where it was raised, with the context only that site had.
  if (error instanceof NadoWithdrawalUserError) {
    return error
  }
  if (error instanceof NadoWithdrawalInputError) {
    logger.error('nado:withdrawal:invalidInput', error, logContext)
    return new Error(i18n.t('withdrawCrypto.errorGeneric'))
  }
  // Already logged at the gateway; the copy steers away from a retry.
  if (error instanceof NadoWithdrawalUnconfirmedError) {
    return new Error(toNadoWithdrawalErrorMessage(error))
  }
  if (!(error instanceof NadoGatewayError)) {
    logger.error(
      'nado:withdrawal:failed',
      error instanceof Error ? error : new Error(String(error)),
      logContext,
    )
    return error
  }
  if (isExpectedNadoWithdrawalFailure(error)) {
    logger.warn('nado:withdrawal:gatewayRejected', logContext)
  } else {
    logger.error('nado:withdrawal:gatewayFailed', error, logContext)
  }
  return new Error(toNadoWithdrawalErrorMessage(error))
}
